import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import type { GridFilterItem } from '@mui/x-data-grid'
import { FilterPanelTrigger, gridFilterModelSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'

import GridSearchOutlined from '@/components/dataDisplay/GridSearchOutlined'
import GridToolbar from '@/components/dataDisplay/GridToolbar'
import CountryFilter from '@/components/datagrid/CountryFilter'
import CustomIconButton from '@/components/inputs/CustomIconButton'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import { getFilterItemValue, updateDataGridFilter } from '@/utils/datagrid/filters'

/**
 * Module augmentation is necessary to use slotProps without TypeScript errors.
 */
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends PartnersDataGridToolbarProps {}
}

const COUNTRY_CODE_FILTER_NAME = 'countryCode'

type PartnersDataGridToolbarProps = {
  countryOptions: CountryIdentifier[]
  onClickAddNewPartner: () => void
}

function PartnersDataGridToolbar({ countryOptions, onClickAddNewPartner }: Readonly<PartnersDataGridToolbarProps>) {
  const { permissions } = usePermissions()
  const { t } = useTranslation()
  const apiRef = useGridApiContext()

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector)
  const countryFilterValue = getFilterItemValue(filterModel, COUNTRY_CODE_FILTER_NAME) ?? ''

  function handleFilterChange(filterName: string, items: GridFilterItem[]) {
    updateDataGridFilter(apiRef, filterName, items[0])
  }

  return (
    <GridToolbar
      actions={
        permissions.has('partnersMutation') ? (
          <CustomIconButton
            Icon={AddOutlinedIcon}
            aria-label={t('partners.add_new_partner')}
            color="primary"
            variant={'solid'}
            onClick={onClickAddNewPartner}
          />
        ) : undefined
      }
      filters={
        <>
          <GridSearchOutlined label={t('common.navigation.search')} placeholder={t('partners.search.placeholder')} />

          <CountryFilter
            countryOptions={countryOptions}
            field="countryCode"
            value={countryFilterValue}
            onChange={(items) => handleFilterChange(COUNTRY_CODE_FILTER_NAME, items)}
          />

          <FilterPanelTrigger />
        </>
      }
    />
  )
}

export default PartnersDataGridToolbar
